import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  Sparkles, 
  Target, 
  ListTodo, 
  MessageSquare,
  AlertTriangle,
  Upload,
  TrendingUp,
  Calendar,
  ChevronRight,
  Trophy,
  Star
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistDashboard = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentAchievements, setRecentAchievements] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashRes, achievementsRes, tasksRes] = await Promise.all([
        api.get('/artist/dashboard'),
        api.get('/artist/achievements'),
        api.get('/artist/tasks')
      ]);
      
      setDashboardData(dashRes.data);
      setRecentAchievements(achievementsRes.data.slice(0, 3));
      setPendingTasks(tasksRes.data.filter(t => t.status !== 'completed').slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, link }) => (
    <Link to={link}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Card className={cn(
          "p-5 cursor-pointer transition-all hover:shadow-lg",
          isDark ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "hover:border-slate-300"
        )}>
          <div className="flex items-center gap-4">
            <div className={cn("p-3 rounded-xl", color)}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                {value}
              </p>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                {label}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    </Link>
  );

  if (loading) {
    return (
      <ArtistLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
        </div>
      </ArtistLayout>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <ArtistLayout>
      <div className="space-y-6 animate-fadeIn" data-testid="artist-dashboard">
        {/* Welcome Section */}
        <div className={cn(
          "rounded-2xl p-6 md:p-8",
          "bg-gradient-to-r from-[#331062] to-[#5a1d8a]"
        )}>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-[#CCFF00] flex items-center justify-center overflow-hidden shadow-lg">
              {dashboardData?.profile_pic_url ? (
                <img 
                  src={`${BACKEND_URL}${dashboardData.profile_pic_url}`} 
                  alt={user?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-[#331062] font-bold text-3xl">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-white font-outfit mb-2">
                Welcome back, {user?.name}! 👋
              </h1>
              <p className="text-white/70">
                {dashboardData?.designer?.role || 'Artist'} • {dashboardData?.designer?.vertical_team || 'Team'}
              </p>
              {dashboardData?.designer?.location && (
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm text-white">
                  📍 {dashboardData.designer.location}
                </span>
              )}
            </div>
            <Link to="/artist/profile">
              <Button className="bg-[#CCFF00] text-[#331062] hover:bg-[#b8e600] font-medium">
                View Profile
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard 
            icon={Sparkles} 
            label="Achievements" 
            value={stats.achievements || 0}
            color="bg-gradient-to-br from-purple-500 to-purple-700"
            link="/artist/achievements"
          />
          <StatCard 
            icon={Target} 
            label="Pending Goals" 
            value={stats.pending_goals || 0}
            color="bg-gradient-to-br from-blue-500 to-blue-700"
            link="/artist/goals"
          />
          <StatCard 
            icon={Trophy} 
            label="Completed Goals" 
            value={stats.completed_goals || 0}
            color="bg-gradient-to-br from-green-500 to-green-700"
            link="/artist/goals"
          />
          <StatCard 
            icon={ListTodo} 
            label="Pending Tasks" 
            value={stats.pending_tasks || 0}
            color="bg-gradient-to-br from-orange-500 to-orange-700"
            link="/artist/tasks"
          />
          <StatCard 
            icon={Star} 
            label="Good Feedback" 
            value={stats.positive_feedback || 0}
            color="bg-gradient-to-br from-yellow-500 to-yellow-700"
            link="/artist/feedback"
          />
          <StatCard 
            icon={AlertTriangle} 
            label="Escalations" 
            value={stats.escalations || 0}
            color="bg-gradient-to-br from-red-500 to-red-700"
            link="/artist/feedback"
          />
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <h3 className={cn("text-lg font-semibold mb-4 font-outfit", isDark ? "text-white" : "text-slate-800")}>
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/artist/screenshots">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                  <Upload className="w-6 h-6 text-[#331062]" />
                  <span className="text-sm">Upload Screenshot</span>
                </Button>
              </Link>
              <Link to="/artist/tasks">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                  <ListTodo className="w-6 h-6 text-[#331062]" />
                  <span className="text-sm">Add Task</span>
                </Button>
              </Link>
              <Link to="/artist/achievements">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                  <Sparkles className="w-6 h-6 text-[#331062]" />
                  <span className="text-sm">Log Achievement</span>
                </Button>
              </Link>
              <Link to="/artist/plans">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-[#331062]" />
                  <span className="text-sm">Add Plan</span>
                </Button>
              </Link>
            </div>
          </Card>

          {/* Pending Tasks */}
          <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={cn("text-lg font-semibold font-outfit", isDark ? "text-white" : "text-slate-800")}>
                My Tasks
              </h3>
              <Link to="/artist/tasks" className="text-sm text-[#331062] hover:underline">
                View All
              </Link>
            </div>
            {pendingTasks.length === 0 ? (
              <div className="text-center py-8">
                <ListTodo className={cn("w-12 h-12 mx-auto mb-3", isDark ? "text-slate-600" : "text-slate-300")} />
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  No pending tasks. You're all caught up!
                </p>
                <Link to="/artist/tasks">
                  <Button size="sm" className="mt-3 bg-[#331062] hover:bg-[#2A0D52]">
                    Create Task
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingTasks.map((task) => (
                  <div 
                    key={task.id}
                    className={cn(
                      "p-3 rounded-lg border-l-4",
                      task.priority === 'high' ? "border-red-500 bg-red-50 dark:bg-red-900/20" :
                      task.priority === 'medium' ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20" :
                      "border-green-500 bg-green-50 dark:bg-green-900/20"
                    )}
                  >
                    <p className={cn("font-medium text-sm", isDark ? "text-white" : "text-slate-800")}>
                      {task.title}
                    </p>
                    {task.due_date && (
                      <p className={cn("text-xs mt-1 flex items-center gap-1", isDark ? "text-slate-400" : "text-slate-500")}>
                        <Calendar className="w-3 h-3" />
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Recent Achievements */}
        <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={cn("text-lg font-semibold font-outfit", isDark ? "text-white" : "text-slate-800")}>
              Recent Achievements
            </h3>
            <Link to="/artist/achievements" className="text-sm text-[#331062] hover:underline">
              View All
            </Link>
          </div>
          {recentAchievements.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className={cn("w-12 h-12 mx-auto mb-3", isDark ? "text-slate-600" : "text-slate-300")} />
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                No achievements yet. Start logging your wins!
              </p>
              <Link to="/artist/achievements">
                <Button size="sm" className="mt-3 bg-[#331062] hover:bg-[#2A0D52]">
                  Log Achievement
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {recentAchievements.map((achievement) => (
                <motion.div
                  key={achievement.id}
                  whileHover={{ scale: 1.02 }}
                  className={cn(
                    "p-4 rounded-xl border",
                    isDark ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">
                      {achievement.type === 'innovation' ? '💡' :
                       achievement.type === 'award' ? '🏆' :
                       achievement.type === 'learning' ? '📚' :
                       achievement.type === 'milestone' ? '🎯' : '⭐'}
                    </span>
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full capitalize",
                      isDark ? "bg-slate-600 text-slate-200" : "bg-slate-100 text-slate-600"
                    )}>
                      {achievement.type}
                    </span>
                  </div>
                  <h4 className={cn("font-medium text-sm mb-1", isDark ? "text-white" : "text-slate-800")}>
                    {achievement.title}
                  </h4>
                  <p className={cn("text-xs line-clamp-2", isDark ? "text-slate-400" : "text-slate-500")}>
                    {achievement.description}
                  </p>
                </motion.div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </ArtistLayout>
  );
};

export default ArtistDashboard;
